import type { ToolCallProgress } from "schemas/generated/chatMessage";

import React from "react";

import { Spinner } from "@replo/design-system/components/spinner/Spinner";

import LoadingVersionCard from "../../history/LoadingVersionCard";
import { VersionCard } from "../../history/VersionHistory";
import { useViewingVersion } from "../hooks/useViewingVersion";
import { getStepsFromProgress } from "../utils/toolCallProgress";
import CanceledCard from "./CanceledCard";
import ErrorCard from "./ErrorCard";
import ProgressCard from "./ProgressCard";

interface ChangeBlockProps {
  isLoading?: boolean;
  progress?: ToolCallProgress;
  showCondensedProgress?: boolean;
  fileVersionId?: string;
  appliedDiffs?: string[];
  isCanceled?: boolean;
}

const CodeChangeBlock = ({
  isLoading,
  progress,
  showCondensedProgress,
  fileVersionId,
  appliedDiffs,
  isCanceled,
}: ChangeBlockProps) => {
  const {
    fileVersion,
    isFileVersionLoading,
    isCurrentVersion,
    isViewingVersion,
  } = useViewingVersion(fileVersionId);

  const progressSteps = progress ? getStepsFromProgress(progress) : undefined;

  if (isCanceled) {
    return <CanceledCard />;
  }

  if (isLoading) {
    return (
      <ProgressCard
        endEnhancer={
          !showCondensedProgress ? undefined : (
            <Spinner variant="ai" size={16} />
          )
        }
        progress={progressSteps}
        showCondensedProgress={showCondensedProgress}
      />
    );
  }

  if (!fileVersion) {
    if (isFileVersionLoading) {
      return <LoadingVersionCard />;
    }

    return (
      <ErrorCard
        title="We couldn't make that change"
        subtitle="Something went wrong while applying your edit. Please try again or reach out to us at support@replo.com"
      />
    );
  }

  return (
    <VersionCard
      version={fileVersion}
      isViewingVersion={isViewingVersion}
      isCurrentVersion={isCurrentVersion}
      appliedDiffs={appliedDiffs}
    />
  );
};

export default CodeChangeBlock;
